import "./AdvancedSearchFilter.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaFilter } from "react-icons/fa";

import * as strings from "../../../strings";

import Button from "../../common/Button/Button";
import Modal from "../../container/Modal/Modal";
import Text from "../../common/Text/Text";
import Link from "../../nav/Link/Link";
import SearchFilter, { SearchFilterProps } from "../SearchFilter/SearchFilter";
import AdvancedFilterItem, {
	AdvancedFilterItemToggleCallback,
} from "./sub-components/AdvancedFilterItem";

import { Translatable } from "../../../lib/util/Translatable";

export interface AdvancedOption {
	title: string;
	value: string;
	depth: number;
	spaced?: boolean;
	children?: AdvancedOption[];
}

interface AdvancedProps {
	id: string;
	checker: (item: string) => boolean;
	onToggle?: AdvancedFilterItemToggleCallback;
	onChange?: (id: string, selectedOptions: AdvancedOption[]) => void;
	options: AdvancedOption[];
	property: string;
	selectedOptions: string[] | null;
	title?: string | Translatable;
	visible?: boolean;
	onSelectAll?: () => void;
	onClear?: () => void;
	indeterminateChecker?: (item: string) => boolean;
	withoutSubOptsTitle: Translatable | string;
}

export interface AdvancedSearchValue {
	search: string | undefined;
	selectedOptions: Record<string, string[] | null>;
}

interface AdvancedSearchFilterProps
	extends Omit<SearchFilterProps, "onChange"> {
	advancedProps: AdvancedProps[];
	onChange?: (value: AdvancedSearchValue) => void;
	noInput?: boolean;
	advancedCtaCopy?: string | Translatable;
}

interface SubSearchFilterProps extends Omit<SearchFilterProps, "onChange"> {
	id: string;
	onChange?: (id: string, value: string | undefined) => void;
}

const SubSearchFilter: React.FC<SubSearchFilterProps> = (props) => {
	const handleChange = useCallback(
		(value: string | undefined) => {
			if (!props.onChange) {
				return;
			}

			props.onChange(props.id, value);
		},
		[props],
	);

	return <SearchFilter {...props} onChange={handleChange} />;
};

export const flattenAdvancedOptions = (
	options: AdvancedOption[],
	filter?: string | undefined,
	result: AdvancedOption[] = [],
) => {
	for (const option of options) {
		if (!filter || option.title.toLowerCase().includes(filter.toLowerCase())) {
			result.push(option);
		}
		if (option.children) {
			flattenAdvancedOptions(option.children, filter, result);
		}
	}
	return result;
};

const AdvancedSearchFilter: React.FC<AdvancedSearchFilterProps> = (props) => {
	const [advancedVisible, setAdvancedVisible] = useState(false);
	const [hasAdvanced, setHasAdvanced] = useState(false);
	const [searchValue, setSearchValue] = useState<string | undefined>();
	const [subFilters, setSubFilters] = useState<Record<string, string>>({});

	const { advancedProps, onChange, noInput } = props;

	useEffect(() => {
		setHasAdvanced(
			advancedProps
				? advancedProps.some((advancedProp) => advancedProp.visible)
				: false,
		);
	}, [advancedProps]);

	const handleToggleAdvanced = useCallback(
		(evt: React.MouseEvent) => {
			evt.preventDefault();
			setAdvancedVisible(!advancedVisible);
		},
		[advancedVisible, setAdvancedVisible],
	);

	const handleSubFilterChange = useCallback((id, value) => {
		setSubFilters((subFilters) =>
			Object.assign({}, subFilters, { [id]: value }),
		);
	}, []);

	const flatDisplayedOptions = useMemo(() => {
		return advancedProps.reduce((flatDisplayedOptions, advancedProp) => {
			flatDisplayedOptions[advancedProp.id] = flattenAdvancedOptions(
				advancedProp.options,
				subFilters[advancedProp.id],
			);
			return flatDisplayedOptions;
		}, {} as Record<string, AdvancedOption[]>);
	}, [advancedProps, subFilters]);

	const handleSearchChange = useCallback(
		(searchValue: string | undefined) => {
			if (onChange) {
				setSearchValue(searchValue);
				onChange({
					search: searchValue,
					selectedOptions: advancedProps.reduce(
						(selectedOptions, props) =>
							Object.assign(selectedOptions, {
								[props.id]: props.selectedOptions,
							}),
						{},
					),
				});
			}
		},
		[advancedProps, onChange],
	);

	const handleAdvancedItemChange = useCallback(
		(id, newSelectedOptions) => {
			if (onChange) {
				const change = {
					search: searchValue,
					selectedOptions: advancedProps.reduce((selectedOptions, props) => {
						let selection = props.selectedOptions;
						if (props.id === id) {
							selection = newSelectedOptions;
						}
						selectedOptions[props.id] = selection;
						return selectedOptions;
					}, {} as Record<string, string[] | null>),
				};
				onChange(change);
			}
			const advancedProp = advancedProps.find((prop) => prop.id === id);
			if (advancedProp?.onChange) {
				advancedProp.onChange(id, newSelectedOptions);
			}
		},
		[advancedProps, onChange, searchValue],
	);

	return (
		<>
			<div className={"AdvancedSearchFilter__controls"}>
				{!noInput && (
					<SearchFilter
						{...props}
						onChange={handleSearchChange}
						hasAdvanced={hasAdvanced}
					/>
				)}
				{hasAdvanced && (
					<Button
						icon={<FaFilter />}
						className={"AdvancedSearchFilter__advanced-button"}
						onClick={handleToggleAdvanced}
					>
						{props.advancedCtaCopy || strings.ADVANCED_SEARCH_SHOW_FILTERS}
					</Button>
				)}
			</div>
			<Modal
				visible={advancedVisible}
				onVisibleChange={setAdvancedVisible}
				className={"AdvancedSearchFilter__advanced"}
				padding={true}
			>
				{advancedProps?.map((advancedProp, index) => {
					if (!advancedProp.visible) {
						return null;
					}

					return (
						<React.Fragment key={advancedProp.id}>
							<Text tag={"h4"} style={{ marginBottom: "15px" }}>
								{advancedProp.title}
							</Text>
							<SubSearchFilter
								searchProps={["title"]}
								onChange={handleSubFilterChange}
								id={advancedProp.id}
								placeholder={strings.ADVANCED_SEARCH_GROUPS_PLACEHOLDER}
								open={advancedVisible}
							/>
							<div className={"AdvancedSearchFilter__links"}>
								{advancedProp.onSelectAll && (
									<Link
										className={"AdvancedSearchFilter__link"}
										onClick={advancedProp.onSelectAll}
									>
										{strings.MISC_SELECT_ALL}
									</Link>
								)}
								{advancedProp.onClear && (
									<Link
										className={"AdvancedSearchFilter__link"}
										onClick={advancedProp.onClear}
									>
										{strings.MISC_CLEAR}
									</Link>
								)}
							</div>
							{flatDisplayedOptions[advancedProp.id].map((option) => {
								return (
									<AdvancedFilterItem
										id={advancedProp.id}
										style={{
											marginLeft: `${(option.depth + 1) * 15 || 0}px`,
											marginBottom: `${option.spaced ? 15 : 0}px`,
										}}
										onChange={handleAdvancedItemChange}
										onToggle={advancedProp.onToggle}
										option={option}
										options={advancedProp.options}
										checker={advancedProp.checker}
										property={advancedProp.property}
										hasChildren={Boolean(option.children?.length)}
										selectedOptions={advancedProp.selectedOptions}
										key={`AdvancedFilter_${option.value}`}
										indeterminateFunc={
											advancedProp.indeterminateChecker
												? advancedProp.indeterminateChecker
												: null
										}
										withoutSubOptsTitle={advancedProp.withoutSubOptsTitle}
									/>
								);
							})}
						</React.Fragment>
					);
				})}
			</Modal>
		</>
	);
};

export default AdvancedSearchFilter;
